import React, { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Sparkles,
  Heart,
  Shirt,
  X,
  ZoomIn,
  Utensils
} from 'lucide-react'

interface Solution {
  id: string
  name: string
  title: string
  description: string
  icon: React.ElementType
  color: string
  bgColor: string
  stats: {
    label: string
    value: string
    growth?: string
  }[]
  features: string[]
  technologies: string[]
  image?: string
}

export const SolutionsShowcase: React.FC = () => {
  const { t, i18n } = useTranslation('homepage')
  
  const solutions: Solution[] = useMemo(() => [
    {
      id: 'beauty',
      name: t('solutionsShowcase.solutions.beauty.name'),
      title: t('solutionsShowcase.solutions.beauty.title'),
      description: t('solutionsShowcase.solutions.beauty.description'),
      icon: Sparkles,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      stats: [],
      features: t('solutionsShowcase.solutions.beauty.features', { returnObjects: true }) as string[],
      technologies: ['OpenAI GPT', 'WhatsApp API', 'Capacitor', 'Vue 3'],
      image: '/images/salao_beleza.png'
    },
    {
      id: 'petshop',
      name: t('solutionsShowcase.solutions.petshop.name'),
      title: t('solutionsShowcase.solutions.petshop.title'),
      description: t('solutionsShowcase.solutions.petshop.description'),
      icon: Heart,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      stats: [],
      features: t('solutionsShowcase.solutions.petshop.features', { returnObjects: true }) as string[],
      technologies: ['Spring Boot', 'PostgreSQL', 'Quasar', 'Docker'],
      image: '/images/pet_shop.png'
    },
    {
      id: 'fashion',
      name: t('solutionsShowcase.solutions.fashion.name'),
      title: t('solutionsShowcase.solutions.fashion.title'),
      description: t('solutionsShowcase.solutions.fashion.description'),
      icon: Shirt,
      color: 'text-amber-700',
      bgColor: 'bg-amber-50',
      stats: [],
      features: t('solutionsShowcase.solutions.fashion.features', { returnObjects: true }) as string[],
      technologies: ['React', 'Instagram API', 'OpenAI', 'Tailwind CSS'],
      image: '/images/loja_roupas.png'
    },
    {
      id: 'bares',
      name: t('solutionsShowcase.solutions.bares.name'),
      title: t('solutionsShowcase.solutions.bares.title'),
      description: t('solutionsShowcase.solutions.bares.description'),
      icon: Utensils,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      stats: [],
      features: t('solutionsShowcase.solutions.bares.features', { returnObjects: true }) as string[],
      technologies: ['React', 'Spring Boot', 'PostgreSQL', 'SSE', 'PWA'],
      image: '/images/bares/02-site-cardapio-desktop.png'
    }
  ], [t, i18n.language])
  
  const [selectedSolution, setSelectedSolution] = useState<Solution>(solutions[0])
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Update selected solution when language changes
  useEffect(() => {
    setSelectedSolution(solutions[0])
  }, [solutions])

  return (
    <section className="py-24 bg-white">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            {t('solutionsShowcase.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('solutionsShowcase.subtitle')}
          </motion.p>
        </div>

        {/* Solution Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {solutions.map((solution) => (
            <motion.button
              key={solution.id}
              onClick={() => setSelectedSolution(solution)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                selectedSolution.id === solution.id
                  ? 'bg-kylix-secondary text-white shadow-lg scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <solution.icon className="inline-block w-5 h-5 mr-2" />
              {solution.name}
            </motion.button>
          ))}
        </div>

        {/* Solution Detail */}
        <motion.div
          key={selectedSolution.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-6xl mx-auto"
        >
          {/* Header with badge, title and description */}
          <div className="text-center mb-12">
            <div className={`inline-flex items-center gap-3 px-6 py-3 rounded-full ${selectedSolution.bgColor} mb-6`}>
              <selectedSolution.icon className={`w-6 h-6 ${selectedSolution.color}`} />
              <span className={`font-semibold text-lg ${selectedSolution.color}`}>
                {selectedSolution.name}
              </span>
            </div>
            
            <h3 className="text-4xl font-bold text-gray-900 mb-6">
              {selectedSolution.title}
            </h3>
            
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              {selectedSolution.description}
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            {/* Left Content - Image (2/3 width) */}
            <div className="lg:col-span-2 relative">
              {selectedSolution.image && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6 }}
                  className="relative group cursor-pointer"
                  onClick={() => setIsModalOpen(true)}
                >
                  <img
                    src={selectedSolution.image}
                    alt={selectedSolution.title}
                    className="w-full h-auto rounded-2xl shadow-2xl transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300 rounded-2xl flex items-center justify-center">
                    <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </motion.div>
              )}
            </div>

            {/* Right Content - Features (1/3 width) */}
            <div className="lg:col-span-1">
              {/* Feature Cards */}
              <div className="space-y-4 mb-8">
                <h4 className="text-xl font-semibold text-gray-900 mb-6">Recursos Principais</h4>
                {selectedSolution.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-4 p-4 bg-gradient-to-r from-kylix-background to-white rounded-xl border border-gray-100 hover:shadow-md transition-all duration-300 hover:border-kylix-secondary"
                  >
                    <div className={`w-3 h-3 ${selectedSolution.bgColor} ${selectedSolution.color.replace('text-', 'border-')} border-2 rounded-full mt-1 flex-shrink-0`}></div>
                    <p className="text-gray-700 text-sm leading-relaxed">{feature}</p>
                  </motion.div>
                ))}
              </div>

              {/* Technologies */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Tecnologias</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedSolution.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-2 bg-kylix-lightgray text-kylix-gray rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors duration-200"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 text-center p-8 bg-gradient-to-r from-kylix-background to-kylix-lightgray rounded-2xl"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {t('solutionsShowcase.cta.bottom.title')}
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            {t('solutionsShowcase.cta.bottom.description')}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button 
              className="btn-primary"
              onClick={() => window.location.href = '/contact'}
            >
              {t('solutionsShowcase.cta.bottom.customDemo')}
            </button>
          </div>
        </motion.div>

        {/* Image Modal */}
        <AnimatePresence>
          {isModalOpen && selectedSolution.image && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
              onClick={() => setIsModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 500 }}
                className="relative max-w-7xl max-h-full"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="absolute -top-4 -right-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors duration-200 z-10"
                >
                  <X className="w-6 h-6 text-gray-600" />
                </button>
                <img
                  src={selectedSolution.image}
                  alt={selectedSolution.title}
                  className="max-w-full max-h-full rounded-xl shadow-2xl"
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  )
}